import {
  Box,
  Flex,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  useColorMode,
  useColorStyle,
} from '@tonic-one/react';
import { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import CSpinner from '../../../components/CSpinner';
import { PolicyTypeEnum } from '../../Policy/utils/constants';
import ApprovedHeaderFields from './ApprovedHeaderFields';
import ApprovedUsers from './ApprovedUsers';
import ApprovedSites from './ApprovedSites';
import ApprovedOneDriveUsers from './ApprovedOneDriveUsers';
import ApprovedTeams from './ApprovedTeams';
import BlockedList from './BlockedList';
import { PermissionNameEnum } from '@Types/permissions';
import { usePermissionStore } from '@Models/usePermissionModel';
import { useOrgStore } from '@Models/useOrgModel';
import { useProvisionedServicesQuery } from '@Dashboard/ThreatDetection/useProvisionedServicesQuery';

const ApprovedAndBlockedList = () => {
  const { t } = useTranslation();
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const { widthEditablePermission } = usePermissionStore();
  const withAdministratorEditPermission = widthEditablePermission(
    PermissionNameEnum.administrator
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [activeTabIndex, setActiveTabIndex] = useState<number>(0);
  const { orgId } = useOrgStore();

  // Use React Query for fetching provisioned services
  const { data: provisionedServices = [] } = useProvisionedServicesQuery(orgId);
  const hasSharePointProvisioned = useMemo(
    () => provisionedServices.includes(PolicyTypeEnum.SharePoint),
    [provisionedServices]
  );
  const hasOneDriveProvisioned = useMemo(
    () => provisionedServices.includes(PolicyTypeEnum.OneDrive),
    [provisionedServices]
  );
  const hasTeamsProvisioned = useMemo(
    () => provisionedServices.includes(PolicyTypeEnum.Teams),
    [provisionedServices]
  );

  // Reset to Exchange tab (index 0) when the current tab is no longer visible
  useEffect(() => {
    // Calculate the actual tab index based on which conditional tabs are visible
    let maxTabIndex = 1; // Exchange (0) + Gmail (1) are always shown
    if (hasSharePointProvisioned) maxTabIndex++;
    if (hasOneDriveProvisioned) maxTabIndex++;
    if (hasTeamsProvisioned) maxTabIndex++;
    if (activeTabIndex > maxTabIndex) {
      setActiveTabIndex(0);
    }
  }, [
    hasSharePointProvisioned,
    hasOneDriveProvisioned,
    hasTeamsProvisioned,
    activeTabIndex,
  ]);

  return (
    <Flex flex={1}>
      {loading && <CSpinner />}
      <Tabs flex={1} index={activeTabIndex} onChange={setActiveTabIndex}>
        <TabList borderBottom={1} borderColor={colorStyle.divider}>
          <Tab>{t('common.service.exchange')}</Tab>
          <Tab>{t('common.service.gmail')}</Tab>
          {hasSharePointProvisioned && (
            <Tab>{t('common.service.sharePoint')}</Tab>
          )}
          {hasOneDriveProvisioned && <Tab>{t('common.service.oneDrive')}</Tab>}
          {hasTeamsProvisioned && <Tab>{t('common.service.teams')}</Tab>}
        </TabList>
        <TabPanels height="100%">
          <TabPanel>
            {(args: any) =>
              !args.isSelected ? null : (
                <Box px="4x">
                  <Box mb={32}>
                    <ApprovedUsers
                      policyType={PolicyTypeEnum.Exchange}
                      withEditPermission={withAdministratorEditPermission}
                      loading={loading}
                      setLoading={setLoading}
                    />
                  </Box>
                  <Box mb={32}>
                    <ApprovedHeaderFields
                      policyType={PolicyTypeEnum.Exchange}
                      withEditPermission={withAdministratorEditPermission}
                      loading={loading}
                      setLoading={setLoading}
                    />
                  </Box>
                  <Box mb={32}>
                    <BlockedList
                      policyType={PolicyTypeEnum.Exchange}
                      withEditPermission={withAdministratorEditPermission}
                      loading={loading}
                      setLoading={setLoading}
                    />
                  </Box>
                </Box>
              )
            }
          </TabPanel>
          <TabPanel>
            {(args: any) =>
              !args.isSelected ? null : (
                <Box px="4x">
                  <ApprovedHeaderFields
                    policyType={PolicyTypeEnum.Gmail}
                    withEditPermission={withAdministratorEditPermission}
                    loading={loading}
                    setLoading={setLoading}
                  />
                </Box>
              )
            }
          </TabPanel>
          {hasSharePointProvisioned && (
            <TabPanel height="100%">
              {(args: any) =>
                !args.isSelected ? null : (
                  <ApprovedSites
                    policyType={PolicyTypeEnum.SharePoint}
                    withEditPermission={withAdministratorEditPermission}
                    loading={loading}
                    setLoading={setLoading}
                  />
                )
              }
            </TabPanel>
          )}
          {hasOneDriveProvisioned && (
            <TabPanel height="100%">
              {(args: any) =>
                !args.isSelected ? null : (
                  <ApprovedOneDriveUsers
                    policyType={PolicyTypeEnum.OneDrive}
                    withEditPermission={withAdministratorEditPermission}
                    loading={loading}
                    setLoading={setLoading}
                  />
                )
              }
            </TabPanel>
          )}
          {hasTeamsProvisioned && (
            <TabPanel height="100%">
              {(args: any) =>
                !args.isSelected ? null : (
                  <ApprovedTeams
                    policyType={PolicyTypeEnum.Teams}
                    withEditPermission={withAdministratorEditPermission}
                    loading={loading}
                    setLoading={setLoading}
                  />
                )
              }
            </TabPanel>
          )}
        </TabPanels>
      </Tabs>
    </Flex>
  );
};
export default ApprovedAndBlockedList;
